// @ts-strict-ignore
/** @jsx jsx */
import React, { useMemo } from 'react'
import { jsx } from 'theme-ui'
import ConditionalHydrate from '../helpers/conditional-hydrate'
import { useBrand, useMeta, useDataSelector } from '../helpers/selectors'
import { Type } from '@uswitch/spark.type'
import ImgixImg from '../imgix-img'

const maxMobileProviderNumbers = 3

const shuffle = (array = []) => {
  array.forEach((_, index) => {
    const randomIndex = Math.floor(Math.random() * index)
    ;[array[index], array[randomIndex]] = [array[randomIndex], array[index]]
  })

  return array
}

const sortProviders = (a, b) => (a.name < b.name ? -1 : 1)
const createGridConfiguration = (numberOfItemsToDisplay) => {
  const maxItemsPerRow = 6
  const maxNumberOfRows = 3
  const { rows, columnsPerRow } = distributeColumnsAcrossRows(
    numberOfItemsToDisplay,
    maxItemsPerRow,
    maxNumberOfRows,
  )

  return {
    rows,
    columnsPerRow,
    columnStyle: {
      gridTemplateColumns: [
        `repeat(${maxMobileProviderNumbers * 2}, minmax(0, 1fr))`,
        `repeat(${maxItemsPerRow * 2}, minmax(0, 1fr))`,
      ],
    },
  }
}

const distributeColumnsAcrossRows = (
  numberOfItemsToDisplay,
  maxNumberOfItemsPerRow,
  maxNumberOfRows,
) => {
  const multipleRowsRequired = numberOfItemsToDisplay > maxNumberOfItemsPerRow
  const isSingleColumn = maxNumberOfItemsPerRow === 1

  if (isSingleColumn) {
    return { columnsPerRow: 2, rows: numberOfItemsToDisplay, finalRowColumns: 0 }
  }

  if (!multipleRowsRequired) {
    return { columnsPerRow: maxNumberOfItemsPerRow, rows: 1, finalRowColumns: 0 }
  }

  let currentSmallestDifferenceByItemsPerRow
  for (let itemsPerRow = maxNumberOfItemsPerRow; itemsPerRow > 1; itemsPerRow--) {
    const itemsInFinalRow = numberOfItemsToDisplay % itemsPerRow
    const differenceBetweenLastTwoRows = itemsPerRow - itemsInFinalRow
    const numberOfRowsRequired = Math.ceil(numberOfItemsToDisplay / maxNumberOfItemsPerRow)
    const itemsPerRowDoesNotExceedMaxPerRow = itemsPerRow <= maxNumberOfItemsPerRow

    // No better possibilites available within row restraints
    if (numberOfRowsRequired > maxNumberOfRows) {
      if (currentSmallestDifferenceByItemsPerRow) {
        return currentSmallestDifferenceByItemsPerRow
      } else {
        return {
          columnsPerRow: maxNumberOfItemsPerRow,
          rows: maxNumberOfRows,
          finalRowColumns: maxNumberOfItemsPerRow,
        }
      }
    }

    if (itemsInFinalRow === 0 && itemsPerRowDoesNotExceedMaxPerRow) {
      return {
        columnsPerRow: maxNumberOfItemsPerRow,
        rows: numberOfRowsRequired,
        finalRowColumns: itemsInFinalRow,
      }
    }

    if (
      !currentSmallestDifferenceByItemsPerRow ||
      (currentSmallestDifferenceByItemsPerRow.columnsPerRow > itemsInFinalRow &&
        currentSmallestDifferenceByItemsPerRow.differenceBetweenLastTwoRows >
          differenceBetweenLastTwoRows)
    ) {
      currentSmallestDifferenceByItemsPerRow = {
        columnsPerRow: maxNumberOfItemsPerRow,
        rows: numberOfRowsRequired,
        finalRowColumns: itemsInFinalRow,
        differenceBetweenLastTwoRows: differenceBetweenLastTwoRows,
      }
    }

    // Final iteration
    if (itemsPerRow === 2) {
      return currentSmallestDifferenceByItemsPerRow
    }
  }
}

const overrideProviderOrder = (providers, overrideOrder) => {
  const overridePriority = [...overrideOrder].reverse()
  const reOrderedProviders = [...providers]

  overridePriority.forEach((override) => {
    const indexOfOverride = reOrderedProviders.findIndex((provider) => {
      return provider.name.toLowerCase() === override.toLowerCase()
    })

    if (indexOfOverride !== -1) {
      reOrderedProviders.unshift(reOrderedProviders.splice(indexOfOverride, 1)[0])
    }
  })

  return reOrderedProviders
}

export const ProvidersLogoModule = ({
  providers,
  orderOverride,
  clickable = false,
  variant,
  maxProvidersToDisplay: maxProviders,
}) => {
  const brand = useBrand()
  const defaultMaxProviders = 10
  const metaMaxProviders = useMeta(['providers', 'maxProvidersToDisplay'], defaultMaxProviders)
  const maxProvidersToDisplay = maxProviders || metaMaxProviders
  const isCi = useDataSelector(['isCi'])

  let shuffledProviders = useMemo(() => shuffle(providers), [providers])
  if (!providers) {
    return null
  }
  if (isCi) shuffledProviders = providers?.sort(sortProviders)

  const orderedProviders = (
    orderOverride ? overrideProviderOrder(shuffledProviders, orderOverride) : shuffledProviders
  ).slice(0, maxProvidersToDisplay)

  const sufficientProvidersToDisplay = orderedProviders.length >= 3

  const { rows, columnsPerRow, columnStyle } = createGridConfiguration(orderedProviders.length)

  return (
    <ConditionalHydrate>
      <If condition={sufficientProvidersToDisplay}>
        <div
          sx={{
            display: ['flex', 'grid'],
            overflowX: ['auto', 'hidden'],
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            wordWrap: 'nowrap',
          }}
        >
          <For each='row' index='i' of={Array(rows).fill()}>
            <div
              sx={{
                display: ['inline-flex', 'grid'],
                gridColumnGap: '20px',
                ...columnStyle,
                mb: 20,
                ml: i !== 0 ? [20, 0] : 0,
                whiteSpace: ['pre', 'initial'],
              }}
              key={i}
            >
              <For
                each='provider'
                index='j'
                of={orderedProviders.slice(i * columnsPerRow, (i + 1) * columnsPerRow)}
              >
                <Provider
                  key={provider.id}
                  imageSrc={provider.images[brand] || provider.images.logo}
                  alt={provider.name}
                  providerNumber={i * columnsPerRow + j + 1}
                  clickable={clickable}
                  href={clickable ? provider.link || provider.slug : undefined}
                  variant={variant}
                />
              </For>
            </div>
          </For>
        </div>
      </If>
    </ConditionalHydrate>
  )
}

const ProviderWrapper = ({ as: Component, children, ...props }) => (
  <Component {...props}>{children}</Component>
)

const Provider = ({ imageSrc, alt, clickable, variant, ...props }) => (
  <ProviderWrapper
    as={clickable ? 'a' : 'div'}
    sx={{
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      gridColumn: 'span 2',
      padding: ['sm', 'md'],
      border: '1px solid',
      borderColor: 'grey-20',
      flexDirection: 'column',
      alignItems: 'center',
      color: 'primary',
      ...(clickable && {
        '&:active': {
          borderColor: 'primary',
        },
        '&:hover': {
          borderColor: 'primary',
        },
        '&:focus': {
          borderColor: 'primary',
        },
      }),
      width: ['120px', 'auto'],
      scrollSnapAlign: 'center',
    }}
    {...props}
  >
    <ImgixImg
      src={imageSrc}
      alt={alt}
      sx={{
        maxWidth: ['70px', '102px'],
        maxHeight: ['42px', '48px'],
        width: 'auto',
        height: 'auto',
        margin: '10px',
        alignSelf: 'center',
        ...(clickable && { margin: 0, mb: 'sm' }),
        ...(1 && { margin: ['20px 0', 0], mb: ['20px', 'sm'] }),
      }}
      height={60}
      width={120}
      imgixParams={{ fit: 'fill', fill: 'solid' }}
    />

    <If condition={clickable && variant !== 'no-link-text'}>
      <Type.Body1
        sx={{
          textAlign: 'center',
          whiteSpace: 'break-spaces',
          wordWrap: 'break-spaces',
          fontSize: '16px',
        }}
      >
        {alt}
      </Type.Body1>
    </If>
  </ProviderWrapper>
)
